import DataLoader from 'dataloader'
import { UserAPI } from 'iam/API'
import { zipObject } from 'lodash'
import { Id } from 'types/definitions'

/**
 * Looks up IDs for users.
 */
export interface UserIdLookup {
  /**
   * Looks up private IDs from public IDs.
   *
   * @param publicIds
   * @returns A map of public ID to private ID
   */
  lookupPrivateIds(
    publicIds: readonly Id[],
  ): Promise<Record<string, string | null>>

  /**
   * Looks up public IDs from private IDs.
   *
   * @param privateIds
   * @returns A map of private ID to public ID
   */
  lookupPublicIds(
    privateIds: readonly Id[],
  ): Promise<Record<string, string | null>>
}

/**
 * Creates a User ID Lookup.
 * @constructor
 */
export function UserIdLookup(userAPI: UserAPI): UserIdLookup {
  const privateIdsLoader = new DataLoader<Id, string | null>(_fetchPrivateIds, {
    cache: true,
  })

  const publicIdsLoader = new DataLoader<Id, string | null>(_fetchPublicIds, {
    cache: true,
  })

  return {
    lookupPrivateIds,
    lookupPublicIds,
  }

  async function lookupPrivateIds(
    publicIds: readonly Id[],
  ): Promise<Record<string, string | null>> {
    const validPublicIds = publicIds.filter(Id.some)
    const privateIds = await Promise.all(
      validPublicIds.map((id) => privateIdsLoader.load(id)),
    )
    return zipObject(validPublicIds, privateIds)
  }

  async function lookupPublicIds(
    privateIds: readonly Id[],
  ): Promise<Record<string, string | null>> {
    const validPrivateIds = privateIds.filter(Id.some)
    const publicIds = await Promise.all(
      validPrivateIds.map((id) => publicIdsLoader.load(id)),
    )
    return zipObject(validPrivateIds, publicIds)
  }

  async function _fetchPrivateIds(publicIds: readonly Id[]) {
    const result = await userAPI.lookupPrivateIds(publicIds.filter(Id.some))
    return publicIds.map((publicId) =>
      Id.some(publicId) ? result[publicId] : null,
    )
  }

  async function _fetchPublicIds(privateIds: readonly Id[]) {
    const result = await userAPI.lookupPublicIds(privateIds.filter(Id.some))
    return privateIds.map((privateId) =>
      Id.some(privateId) ? result[privateId] : null,
    )
  }
}
